import { APP_BASE_HREF, PathLocationStrategy, PlatformLocation } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import { isNonEmptyString } from '@accesso/cc-std-lib';

import {
	QUERY_PARAM_DEBUG,
	QUERY_PARAM_LANGUAGE,
	QUERY_PARAM_MERCHANT_ID,
	QUERY_PARAM_PARENT_MERCHANT_ID,
	QUERY_PARAM_RESELLER_ID,
	QUERY_PARAM_RESELLER_LOCATION_ID,
	QUERY_PARAM_STORE_IDENTIFIER,
	QUERY_PARAM_TRADES_FLAG,
	QUERY_PARAM_TRADES_ORIGIN,
	QUERY_PARAM_TRADES_UUID,
} from '../../core/enums/query-params';

const PRESERVED = [
	QUERY_PARAM_LANGUAGE,
	QUERY_PARAM_DEBUG,
	QUERY_PARAM_STORE_IDENTIFIER,
	QUERY_PARAM_RESELLER_LOCATION_ID,
	QUERY_PARAM_RESELLER_ID,
	QUERY_PARAM_MERCHANT_ID,
	QUERY_PARAM_PARENT_MERCHANT_ID,
	QUERY_PARAM_TRADES_FLAG,
	QUERY_PARAM_TRADES_ORIGIN,
	QUERY_PARAM_TRADES_UUID,
];

type QueryParams = Record<string, string>;

@Injectable({
	providedIn: 'root',
})
export class PreserveQueryParametersStrategy extends PathLocationStrategy {
	/**
	 * Preserved query parameters
	 */
	private preserved: QueryParams = {};
	/**
	 * If we have any preserved parameters
	 */
	private hasPreservedParams = false;

	/**
	 * Our constructor
	 * @param platformLocation The platform location service
	 * @param href The optional base href
	 */
	constructor() {
		const platformLocation = inject<PlatformLocation>(PlatformLocation);
		const href = inject(APP_BASE_HREF, { optional: true });

		super(platformLocation, href ?? undefined);

		const params = Object.entries(this.extractQueryParams(platformLocation.search));

		if (params.length) {
			params.forEach(([name, value]) => {
				if (PRESERVED.includes(name)) {
					this.preserved[name] = value;
					this.hasPreservedParams = true;
				}
			});
		}
	}

	/**
	 * Prepares the url for the pushState
	 * @param internal The current url
	 */
	override prepareExternalUrl(internal: string): string {
		const url = super.prepareExternalUrl(internal);

		if (!this.hasPreservedParams) return url;

		const index = url.indexOf('?');
		const query = Object.entries(this.preserved);

		if (index === -1) {
			return url + '?' + query.map(this.entryToQuery).join('&');
		}

		const currentQuery = this.extractQueryParams(url);
		const newQuery = query
			.filter(([key]) => !Object.prototype.hasOwnProperty.call(currentQuery, key))
			.map(this.entryToQuery)
			.join('&');

		return url + (newQuery.length ? '&' : '') + newQuery;
	}

	/**
	 * Converts an etry to a query parameter
	 * @param entry The [key, value] entry
	 */
	private entryToQuery([key, value]: [string, string]): string {
		return `${key}=${encodeURIComponent(value)}`;
	}

	/**
	 * Extracts query parameters from a string
	 * @param path The path
	 */
	private extractQueryParams(path: string): QueryParams {
		const index = path.indexOf('?');
		const params: QueryParams = {};

		if (index === -1) return params;

		path.substring(index + 1)
			.split('&')
			.forEach((param) => {
				const [key, value] = param.split('=');
				if (isNonEmptyString(key) && PRESERVED.includes(key) && value) {
					params[key] = value;
					this.hasPreservedParams = true;
				}
			});

		return params;
	}
}

export function providePreserveQueryParametersStrategy() {
	return {
		provide: PathLocationStrategy,
		useClass: PreserveQueryParametersStrategy,
	};
}
